import type { Phone } from '../types'
import { MetricBadge, type MetricSeverity } from './MetricBadge'
import { DASH } from '../lib/metricFormat'

/** Drop the `android.permission.` prefix so the tooltip stays readable. */
function shortName(permission: string): string {
  return permission.replace(/^android\.permission\./, '')
}

/**
 * Permissions cell — compact "granted/total" badge for the agent's
 * runtime-permission map. Closes the original incident: a renter who
 * never granted a permission is now visible in the admin table instead
 * of silently breaking SIM/metrics.
 *
 * Detail lives in the native `title` tooltip (✓/✗ per permission) — a
 * positioned popover would clip inside the table `<td>` scroll
 * container (see .claude/rules/web-layout.md).
 */
export function PermissionsCell({ phone }: { phone: Phone }) {
  const perms = phone.metrics?.runtimePermissions ?? null

  // null (old agent) or empty map → neutral "no data".
  if (perms === null || Object.keys(perms).length === 0) {
    return <MetricBadge label={DASH} severity="neutral" title="Разрешения агентом не присылались" />
  }

  const entries = Object.entries(perms).sort(([a], [b]) => a.localeCompare(b))
  const granted = entries.filter(([, ok]) => ok).length
  const total = entries.length
  const severity: MetricSeverity = granted === total ? 'ok' : 'critical'
  const title = entries
    .map(([name, ok]) => `${ok ? '✓' : '✗'} ${shortName(name)}`)
    .join('\n')

  return <MetricBadge label={`${granted}/${total}`} severity={severity} title={title} />
}
